// The one-tap upload speed test: random bytes made in the browser, sent to the upload Worker's discard
// endpoint (POST /speed on up[-staging].photohawk-cdn.com — the same edge real uploads use; it reads the
// body and drops it, CORS for photohawk pages only, ≤25 MB a request, rate-limited per IP) over a few
// parallel lanes for about 8 seconds. Never started automatically: it uses the visitor's data allowance,
// at most SPEED_TEST_MAX_BYTES.
import { getApiBaseDomain } from "@/lib/apiDomain";
import { mbpsFromSamples } from "./uploadEstimate";

export const speedTestUrl = (domain = getApiBaseDomain()): string =>
  domain === "photohawk-staging.com" ? "https://up-staging.photohawk-cdn.com/speed" : "https://up.photohawk-cdn.com/speed";

/** Parallel lanes (HTTP/2 multiplexes them over one connection, like the uploader's own uploads). */
export const SPEED_TEST_LANES = 4;
/** How long the test sends, and when it stops whatever is still in flight. */
export const SPEED_TEST_MS = 8_000;
export const SPEED_TEST_HARD_STOP_MS = 10_000;
/** Most data a test may send: the "Uses up to about … MB of data" note. */
export const SPEED_TEST_MAX_BYTES = 100_000_000;
export const SPEED_TEST_MAX_MB = SPEED_TEST_MAX_BYTES / 1_000_000;
/** Request sizes: start small (slow links still finish one quickly), double while requests are quick. */
export const CHUNK_MIN = 256 * 1024;
export const CHUNK_MAX = 8 * 1024 * 1024; // well under the endpoint's 25 MB cap
const GETRANDOM_MAX = 65_536; // crypto.getRandomValues fills at most 64 KB a call

/** The next request size for a lane: doubled while a request takes under a second, capped. */
export const nextChunkSize = (size: number, tookMs: number): number => (tookMs < 1000 ? Math.min(CHUNK_MAX, size * 2) : size);

/** Random bytes (incompressible), filled in 64 KB chunks. */
export const randomBytes = (n: number, cryptoImpl: Pick<Crypto, "getRandomValues"> = crypto): Uint8Array => {
  const out = new Uint8Array(n);
  for (let off = 0; off < n; off += GETRANDOM_MAX) cryptoImpl.getRandomValues(out.subarray(off, Math.min(n, off + GETRANDOM_MAX)));
  return out;
};

/** Sends `body`, reporting bytes sent as they go; resolves when the server has answered 2xx. */
export type SpeedPost = (body: Uint8Array, onSent: (sentSoFar: number) => void, signal: AbortSignal) => Promise<void>;

export const xhrPost =
  (url: string): SpeedPost =>
  (body, onSent, signal) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      // text/plain keeps it a simple CORS request (no preflight round trip)
      xhr.setRequestHeader("Content-Type", "text/plain");
      xhr.upload.onprogress = (e) => onSent(e.loaded);
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? (onSent(body.length), resolve()) : reject(new Error(`HTTP ${xhr.status}`)));
      xhr.onerror = () => reject(new Error("network"));
      xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.send(new Blob([body]));
    });

export type SpeedTestResult = { mbps: number; bytes: number; ms: number };

/**
 * Runs the test: SPEED_TEST_LANES lanes each send requests back to back until SPEED_TEST_MS has passed or
 * SPEED_TEST_MAX_BYTES has been handed out; whatever is still in flight at SPEED_TEST_HARD_STOP_MS is
 * aborted. `onProgress` gets the live Mbps (over the last two seconds). Rejects when nothing got through.
 */
export const runSpeedTest = async ({
  post = xhrPost(speedTestUrl()),
  onProgress,
  signal,
  now = () => performance.now(),
  durationMs = SPEED_TEST_MS,
  hardStopMs = SPEED_TEST_HARD_STOP_MS,
  maxBytes = SPEED_TEST_MAX_BYTES,
  lanes = SPEED_TEST_LANES,
  bytes = randomBytes(CHUNK_MAX),
}: {
  post?: SpeedPost;
  onProgress?: (mbps: number) => void;
  signal?: AbortSignal;
  now?: () => number;
  durationMs?: number;
  hardStopMs?: number;
  maxBytes?: number;
  lanes?: number;
  bytes?: Uint8Array;
} = {}): Promise<SpeedTestResult> => {
  const stop = new AbortController();
  const onOuterAbort = () => stop.abort();
  signal?.addEventListener("abort", onOuterAbort, { once: true });
  const t0 = now();
  let handedOut = 0;
  let sent = 0;
  const samples: { t: number; bytes: number }[] = [{ t: 0, bytes: 0 }];
  const record = () => {
    const t = now() - t0;
    samples.push({ t, bytes: sent });
    if (onProgress) {
      const recent = samples.filter((s) => s.t >= t - 2000);
      const m = mbpsFromSamples(recent.length >= 2 ? recent : samples, 0);
      if (m != null) onProgress(m);
    }
  };
  const hardStop = setTimeout(() => stop.abort(), hardStopMs);
  let failures = 0;

  const lane = async () => {
    let size = CHUNK_MIN;
    while (!stop.signal.aborted && now() - t0 < durationMs) {
      const n = Math.min(size, CHUNK_MAX, bytes.length, maxBytes - handedOut);
      if (n <= 0) break;
      handedOut += n;
      let last = 0;
      const started = now();
      try {
        await post(bytes.subarray(0, n), (so) => {
          if (so > last) { sent += so - last; last = so; record(); }
        }, stop.signal);
      } catch (e) {
        if (stop.signal.aborted) break;
        failures += 1;
        if (failures >= lanes * 2) { stop.abort(); break; } // the endpoint is refusing (rate limit, offline)
        continue;
      }
      size = nextChunkSize(size, now() - started);
    }
  };

  try {
    await Promise.all(Array.from({ length: lanes }, lane));
  } finally {
    clearTimeout(hardStop);
    signal?.removeEventListener("abort", onOuterAbort);
  }
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");
  const mbps = mbpsFromSamples(samples);
  if (mbps == null || sent === 0) throw new Error("speed test: nothing got through");
  return { mbps, bytes: sent, ms: now() - t0 };
};
